import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import AppShell from "./AppShell";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ArrowRight, MagicWand, GitCompare, Sparks } from "iconoir-react";
import StatusBadge from "@/components/StatusBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { timeAgo } from "@/lib/format";
import QuotaBanner from "@/components/QuotaBanner";

const statTile = "border border-border p-6 bg-card";

export default function Dashboard() {
  const [overview, setOverview] = useState(null);
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      api.get("/dashboard/overview").then((r) => r.data),
      api.get("/audits").then((r) => r.data),
    ])
      .then(([o, a]) => {
        if (!mounted) return;
        setOverview(o);
        setAudits(a);
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const latest = overview?.latest_audit;
  const history = overview?.history || [];

  return (
    <AppShell
      title="Mission control"
      subtitle="// overview"
      actions={
        <Link to="/audits/new" data-testid="dashboard-new-audit-cta" className="px-4 py-2 bg-obsidian text-bone font-app text-xs uppercase tracking-widest font-bold hover:bg-intl transition-colors">
          New audit →
        </Link>
      }
    >
      <QuotaBanner />
      {loading ? (
        <div className="grid grid-cols-12 gap-4 lg:gap-6" data-testid="dashboard-skeleton">
          <div className="col-span-12 md:col-span-6 row-span-2 border border-border p-8 bg-card space-y-4">
            <Skeleton className="h-3 w-32" />
            <Skeleton className="h-20 w-40" />
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-16 w-full" />
          </div>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="col-span-6 md:col-span-3 border border-border p-6 bg-card space-y-3">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-10 w-14" />
            </div>
          ))}
          <div className="col-span-12 lg:col-span-8 border border-border p-6 bg-card">
            <Skeleton className="h-56 w-full" />
          </div>
          <div className="col-span-12 lg:col-span-4 border border-border p-6 bg-card space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
      ) : !latest ? (
        <div className="border border-dashed border-border p-12 text-center bg-card" data-testid="empty-dashboard">
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">// no audits yet</div>
          <h2 className="mt-4 font-marketing text-3xl">Run your first AI Visibility audit.</h2>
          <p className="mt-3 text-muted-foreground max-w-md mx-auto">
            We'll crawl your site and produce a 0-100 score across 9 weighted categories — in about 60 seconds.
          </p>
          <Link to="/audits/new" data-testid="empty-dashboard-cta" className="inline-block mt-6 px-6 py-3 bg-obsidian text-bone font-app text-sm uppercase tracking-wider font-bold hover:bg-intl transition-colors">
            Start free audit
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-4 lg:gap-6" data-testid="dashboard-grid">
          {/* Hero metric */}
          <div className="col-span-12 md:col-span-6 row-span-2 border border-border p-8 bg-card" data-testid="hero-metric-tile">
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">AI Visibility Score</div>
            <div className="mt-4 flex items-end gap-4">
              <div className="font-mono text-7xl lg:text-8xl leading-none">{latest.final_score ?? "—"}</div>
              <div className="font-mono text-xl text-muted-foreground mb-2">/100</div>
            </div>
            <div className="mt-4">
              <StatusBadge label={latest.status_label} large />
            </div>
            {latest.created_at && (
              <div className="mt-3 font-mono text-[11px] uppercase tracking-widest text-muted-foreground" data-testid="last-audit-badge">
                Last audit · {timeAgo(latest.created_at)}
              </div>
            )}
            <p className="mt-6 text-muted-foreground leading-relaxed max-w-md">{latest.summary || "—"}</p>
            <div className="mt-8 flex gap-3">
              <Link to={`/audits/${latest.id}`} data-testid="open-latest-audit" className="px-4 py-2 bg-obsidian text-bone font-app text-xs uppercase tracking-widest font-bold hover:bg-intl transition-colors">
                Open full report
              </Link>
              <Link to={`/audits/${latest.id}/recommendations`} data-testid="open-latest-recs" className="px-4 py-2 border border-border font-app text-xs uppercase tracking-widest font-bold hover:bg-card transition-colors">
                See recommendations
              </Link>
            </div>
          </div>

          {/* Side metrics */}
          <div className={`col-span-6 md:col-span-3 ${statTile}`} data-testid="tile-recommendation-count">
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Recommendations</div>
            <div className="mt-3 font-mono text-4xl">{overview.recommendation_count}</div>
          </div>
          <div className={`col-span-6 md:col-span-3 ${statTile}`} data-testid="tile-fix-count">
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Fixes generated</div>
            <div className="mt-3 font-mono text-4xl">{overview.fix_count}</div>
          </div>
          <div className={`col-span-6 md:col-span-3 ${statTile}`} data-testid="tile-competitor-count">
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Competitors</div>
            <div className="mt-3 font-mono text-4xl">{overview.competitor_count}</div>
          </div>
          <div className={`col-span-6 md:col-span-3 ${statTile}`} data-testid="tile-audit-count">
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Audits run</div>
            <div className="mt-3 font-mono text-4xl">{audits.length}</div>
          </div>

          {/* Chart */}
          <div className="col-span-12 lg:col-span-8 border border-border p-6 bg-card" data-testid="visibility-chart">
            <div className="flex justify-between items-baseline">
              <div>
                <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Visibility trend</div>
                <h3 className="font-app text-xl">Score history</h3>
              </div>
              <Link to="/monitoring" className="font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-intl">View all →</Link>
            </div>
            <div className="mt-6 h-56">
              {history.length < 2 ? (
                <div className="h-full flex items-center justify-center text-muted-foreground font-mono text-sm">Run more audits to see your trend.</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={history.map((h, i) => ({ ...h, i: i + 1 }))}>
                    <XAxis dataKey="i" hide />
                    <YAxis domain={[0, 100]} tick={{ fill: "#5C5C5C", fontFamily: "IBM Plex Mono", fontSize: 11 }} axisLine={false} tickLine={false} width={28} />
                    <Tooltip contentStyle={{ background: "#0A0A0A", color: "#F5F5F0", border: "none", borderRadius: 0, fontFamily: "IBM Plex Mono", fontSize: 12 }} formatter={(v) => [`${v}`, "score"]} labelFormatter={() => ""} />
                    <Line type="monotone" dataKey="score" stroke="#FF4F00" strokeWidth={2} dot={{ r: 3, fill: "#FF4F00", strokeWidth: 0 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Quick actions */}
          <div className="col-span-12 lg:col-span-4 border border-border bg-card p-6" data-testid="quick-actions-tile">
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Quick actions</div>
            <h3 className="font-app text-xl mt-1">Where to focus</h3>
            <div className="mt-6 space-y-3">
              <Link to="/fixes" data-testid="quick-action-fixes" className="flex items-center justify-between p-3 border border-border hover:border-intl hover:bg-background transition-colors">
                <div className="flex items-center gap-3"><MagicWand width={18} height={18} strokeWidth={1.5} /><span>Browse fixes</span></div>
                <ArrowRight width={16} height={16} />
              </Link>
              <Link to="/content" data-testid="quick-action-content" className="flex items-center justify-between p-3 border border-border hover:border-intl hover:bg-background transition-colors">
                <div className="flex items-center gap-3"><Sparks width={18} height={18} strokeWidth={1.5} /><span>Generate content strategy</span></div>
                <ArrowRight width={16} height={16} />
              </Link>
              <Link to="/competitors" data-testid="quick-action-competitors" className="flex items-center justify-between p-3 border border-border hover:border-intl hover:bg-background transition-colors">
                <div className="flex items-center gap-3"><GitCompare width={18} height={18} strokeWidth={1.5} /><span>Track a competitor</span></div>
                <ArrowRight width={16} height={16} />
              </Link>
            </div>
          </div>

          {/* Recent audits */}
          <div className="col-span-12 border border-border bg-card" data-testid="recent-audits-table">
            <div className="px-6 py-4 border-b border-border flex justify-between items-baseline">
              <div>
                <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Recent</div>
                <h3 className="font-app text-xl">Audits</h3>
              </div>
              <Link to="/audits/new" className="font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-intl">Run a new audit →</Link>
            </div>
            <div className="divide-y divide-border">
              {audits.length === 0 ? (
                <div className="p-6 text-muted-foreground text-sm">No audits yet.</div>
              ) : audits.map((a) => (
                <Link key={a.id} to={`/audits/${a.id}`} data-testid={`audit-row-${a.id}`} className="grid grid-cols-12 items-center px-6 py-4 hover:bg-background transition-colors">
                  <div className="col-span-6 md:col-span-5 font-mono text-sm truncate">{a.website_url}</div>
                  <div className="col-span-3 md:col-span-2"><StatusBadge label={a.status_label || (a.status === "complete" ? "" : a.status)} /></div>
                  <div className="col-span-3 md:col-span-2 font-mono text-sm text-right md:text-left">{a.final_score ?? "—"}/100</div>
                  <div className="hidden md:block md:col-span-2 font-mono text-xs text-muted-foreground">{a.created_at?.slice(0, 16).replace("T", " ")}</div>
                  <div className="hidden md:block md:col-span-1 text-right"><ArrowRight width={16} height={16} /></div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
